import * as React from "react";
import { MapPin } from "lucide-react";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { Popover, PopoverAnchor, PopoverContent } from "./popover";
import { cn } from "../lib/utils";

/** One line under the box while somebody types. */
export interface AddressSuggestionItem {
  placeId: string;
  text: string;
  main: string;
  secondary: string;
}

/** The address somebody picked, split for forms that keep the parts apart. */
export interface AddressPick {
  formatted: string;
  street: string;
  postalCode: string;
  city: string;
  region: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
  countryName: string;
  lat: number | null;
  lng: number | null;
  /** IANA zone of the place, when the address service knows it. */
  timeZone: string | null;
}

/** What one search found. `off` = the address service is not set up (no key), so no search will. */
export interface AddressSuggestions {
  suggestions: AddressSuggestionItem[];
  off?: boolean;
}

/** How a host app reaches the address service (each app has its own API client). */
export interface AddressSource {
  suggest: (query: string, session: string) => Promise<AddressSuggestionItem[] | AddressSuggestions>;
  details: (placeId: string, session: string) => Promise<AddressPick | null>;
  /** The line shown under the box once a search says the service is off, in the reader's language. */
  offNote?: string;
}

interface AddressAutocompleteProps {
  value: string;
  /** Every keystroke, like a plain text box: the field still takes an address typed by hand. */
  onChange: (text: string) => void;
  /** A suggestion was picked; the text has already been set to its one-line form. */
  onPick?: (pick: AddressPick) => void;
  source: AddressSource;
  /** What the picked address writes into the box: the whole line, or only the street. */
  fill?: "formatted" | "street";
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /** On the box's wrapper: a host app can make it its own pill (the CRM's `crm-inp`). */
  className?: string;
  /** On the text box itself. */
  inputClassName?: string;
  maxLength?: number;
  /** The attribution line Google's terms require under its suggestions. */
  attribution?: string;
  "aria-label"?: string;
}

const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;

function newSession(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * A text box that suggests real addresses while somebody types, and fills the parts in
 * when one is picked. Still a plain text box underneath: a value typed by hand is kept, and
 * with no address service (no key, the network down) the list never opens. When the service
 * says it is not set up, the source's `offNote` says so under the box, so the field does
 * not look broken.
 *
 * Keyboard as in every combobox: the arrows move in the list, Enter picks, Escape closes
 * it. Focus stays in the box the whole time, so typing never stops.
 */
export function AddressAutocomplete({
  value, onChange, onPick, source, fill = "formatted", multiline = false, rows = 2,
  placeholder, disabled, id, className, inputClassName, maxLength, attribution = "Google", "aria-label": ariaLabel,
}: AddressAutocompleteProps) {
  const autoId = React.useId();
  const listId = `${id ?? autoId}-suggestions`;
  const [items, setItems] = React.useState<AddressSuggestionItem[]>([]);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const [off, setOff] = React.useState(false);
  const session = React.useRef(newSession());
  const asked = React.useRef(0);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Typing after a pick asks again; the pick itself must not.
  const quiet = React.useRef(false);

  React.useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const ask = (text: string) => {
    if (timer.current) clearTimeout(timer.current);
    const query = text.trim();
    if (query.length < MIN_CHARS) {
      setItems([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(() => {
      const n = ++asked.current;
      void source
        .suggest(query, session.current)
        .then((answer) => {
          if (n !== asked.current) return; // a later keystroke already asked
          const found = Array.isArray(answer) ? answer : answer.suggestions;
          setOff(!Array.isArray(answer) && !!answer.off);
          setItems(found);
          setActive(found.length ? 0 : -1);
          setOpen(found.length > 0);
        })
        .catch(() => {
          if (n === asked.current) setOpen(false);
        });
    }, DEBOUNCE_MS);
  };

  const change = (text: string) => {
    onChange(text);
    if (quiet.current) quiet.current = false;
    ask(text);
  };

  const pick = async (item: AddressSuggestionItem) => {
    setOpen(false);
    asked.current++;
    quiet.current = true;
    const token = session.current;
    // A pick ends the billing session; the next typing starts a new one.
    session.current = newSession();
    onChange(item.text);
    const place = await source.details(item.placeId, token).catch(() => null);
    if (!place) return;
    onChange(fill === "street" && place.street ? place.street : place.formatted || item.text);
    onPick?.(place);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      void pick(items[active]!);
    } else if (e.key === "Escape") {
      // Closes the list only, never the dialog the field sits in.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  const fieldProps = {
    id,
    value,
    className: inputClassName,
    placeholder,
    disabled,
    maxLength,
    autoComplete: "off",
    role: "combobox",
    "aria-label": ariaLabel,
    "aria-autocomplete": "list" as const,
    "aria-expanded": open,
    "aria-controls": listId,
    "aria-activedescendant": open && active >= 0 ? `${listId}-${active}` : undefined,
    onKeyDown,
    onBlur: () => setOpen(false),
  };

  return (
    <>
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className={cn("w-full", className)}>
            {multiline ? (
              <Textarea {...fieldProps} rows={rows} onChange={(e) => change(e.target.value)} />
            ) : (
              <Input {...fieldProps} onChange={(e) => change(e.target.value)} />
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="p-1 w-[var(--radix-popover-trigger-width)] min-w-[16rem]"
          // The box keeps the focus: the list is read and clicked, never tabbed into.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <ul id={listId} role="listbox" className="max-h-72 overflow-y-auto">
            {items.map((item, i) => (
              <li
                key={item.placeId}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md px-2.5 py-2 text-sm",
                  i === active && "bg-accent text-accent-foreground",
                )}
                // Mouse down, not click: a click lands after the box's blur has closed the list.
                onMouseDown={(e) => {
                  e.preventDefault();
                  void pick(item);
                }}
                onMouseMove={() => setActive(i)}
              >
                <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{item.main}</span>
                  {item.secondary && <span className="block truncate text-xs text-muted-foreground">{item.secondary}</span>}
                </span>
              </li>
            ))}
          </ul>
          <p className="px-2.5 pb-1 pt-1.5 text-right text-[11px] text-muted-foreground">{attribution}</p>
        </PopoverContent>
      </Popover>
      {off && source.offNote ? <p className="mt-1.5 text-xs text-muted-foreground">{source.offNote}</p> : null}
    </>
  );
}
